import { useFields } from '@hooks'
import { useEffect, useMemo } from 'react'
import { Column, SortingRule, useFlexLayout, useSortBy, useTable } from 'react-table'
import {
  Button,
  ButtonGroup,
  Card,
  Flexbox,
  Icon,
  Modal,
  Search,
  Table,
  Text,
} from '@components/common'
import { GLPost } from '@api/models'
import Pagination from '@components/Pagination'
import { GLPostsHook } from '../useGLPosts'
import { GLPOST_FORM_ID } from '@constants'
import GLPostForm from '../GLPostForm'
import GLPostsTableLoading from './GLPostsTableLoading'

const GLPostsTable = ({
  pageNumber,
  itemsPerPage,
  handleSearch,
  searchValue,
  changePage,
  sortByInitialState,
  updateSortSearchParam,
  //
  searchKey,
  selectedGLPostKeys,
  glPosts,
  isLoadingGLPosts,
  isErrorFetchingGLPosts,
  isRefetchingGLPosts,

  handleDeleteGLPost,
  isLoadingDeleteGLPost,
  openDeleteGLPostModal,
  selectedGLPost,
  deleteGLPostModalIsOpen,
  closeDeleteGLPostModal,
  openEditGLPostModal,
  closeEditGLPostModal,
  editGLPostModalIsOpen,
  handleEditGLPost,
  isLoadingEditGLPost,
  canEdit,
}: GLPostsHook) => {
  // Declare columns
  const { fields, isLoading: isLoadingFields, isError: isErrorGettingFields } = useFields()

  const columns = useMemo<Column<GLPost>[]>(
    () => [
      {
        Header: fields.GL_POST_ID,
        accessor: 'GL_POST_ID',
      },
      {
        Header: fields.GL_DESC,
        accessor: 'GL_DESC',
      },
      {
        Header: '',
        accessor: 'GLCKEY0',
        disableSortBy: true,
        Cell: ({ row }) => (
          <Flexbox alignItems="center" justifyContent="flex-end" style={{ width: '100%' }}>
            <ButtonGroup hidden={!canEdit}>
              <Button
                icon={<Icon name="Edit" />}
                size="compact"
                onClick={e => {
                  e.stopPropagation()
                  openEditGLPostModal(row.original)
                }}
              >
                Edit
              </Button>

              <Button
                icon={<Icon name="Trash2" />}
                size="compact"
                intent="danger"
                onClick={e => {
                  e.stopPropagation()
                  openDeleteGLPostModal(row.original)
                }}
              >
                Delete
              </Button>
            </ButtonGroup>
          </Flexbox>
        ),
      },
    ],
    [isLoadingFields]
  )

  // Declare react table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: glPosts ? glPosts.list : [],
      manualSortBy: true,
      disableMultiSort: true,
      initialState: {
        sortBy: sortByInitialState() as SortingRule<GLPost>[],
      },
    },
    useSortBy,
    useFlexLayout
  )

  // Update sort param
  useEffect(() => {
    updateSortSearchParam(sortBy, setSortBy)
  }, [sortBy])

  // Returns
  const isTableLoading = isLoadingGLPosts || isLoadingFields
  const isError = isErrorFetchingGLPosts || isErrorGettingFields

  if (isTableLoading) {
    return <GLPostsTableLoading />
  }

  if (isError) {
    return <Table.NoResults heading="There was an error." />
  }

  return (
    <>
      <Modal
        isOpen={deleteGLPostModalIsOpen}
        close={closeDeleteGLPostModal}
        title={`Delete ${selectedGLPost?.GL_POST_ID} (${selectedGLPost?.GL_DESC})`}
        width={540}
        action={() => handleDeleteGLPost(selectedGLPost)}
        actionLabel={`Delete GL code`}
        intent="danger"
        actionAutoFocus
        isActionLoading={isLoadingDeleteGLPost}
      >
        <Text p>
          Are you sure you want to delete GL code{' '}
          <strong>
            {selectedGLPost?.GL_POST_ID} ({selectedGLPost?.GL_DESC})
          </strong>
          ? <Text intent="danger">This cannot be undone</Text>.
        </Text>
      </Modal>

      <Modal
        isOpen={editGLPostModalIsOpen}
        close={closeEditGLPostModal}
        title={`Edit GL code ${selectedGLPost?.GL_POST_ID} (${selectedGLPost?.GL_DESC})`}
        width={576}
        actionLabel="Save changes"
        form={GLPOST_FORM_ID}
        isActionLoading={isLoadingEditGLPost}
        dismissLabel={canEdit ? 'Discard changes' : 'Close'}
        actionDisabled={!canEdit}
      >
        <GLPostForm
          isEdit={true}
          initialValues={selectedGLPost}
          handleSubmit={handleEditGLPost}
          selectedGLPostKeys={selectedGLPostKeys}
          canEdit={canEdit}
        />
      </Modal>

      <Card spacing="none">
        <Table.Actions>
          <Flexbox>
            <Search
              onChange={handleSearch}
              placeholder="Search by post code..."
              name={searchKey}
              width={'640px'}
              value={searchValue}
            />
          </Flexbox>
        </Table.Actions>
        {glPosts.list.length === 0 ? (
          <Table.NoResults text="" />
        ) : (
          <>
            <Table
              {...getTableProps()}
              aria-label="Company Table"
              label="Company Table"
              aria-colcount={columns.length}
              aria-rowcount={glPosts.list.length}
              height="calc(100vh - 280px)"
            >
              <Table.Head sticky>
                {isRefetchingGLPosts ? (
                  <Table.LoadingRows />
                ) : (
                  headerGroups.map((headerGroup, i) => (
                    <Table.HeadRow {...headerGroup.getHeaderGroupProps()} key={i}>
                      {headerGroup.headers.map((column, i) => (
                        <Table.Header
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          column={column}
                          key={i}
                          tabIndex={0}
                        >
                          {column.render('Header')}
                        </Table.Header>
                      ))}
                    </Table.HeadRow>
                  ))
                )}
              </Table.Head>

              <Table.Body {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row)
                  return (
                    <Table.Row
                      {...row.getRowProps()}
                      onClick={() => {
                        openEditGLPostModal(row.original)
                      }}
                      tabIndex={0}
                      key={i}
                    >
                      {row.cells.map(cell => (
                        <Table.Cell {...cell.getCellProps()} column={cell.column}>
                          {cell.render('Cell')}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
            <Pagination
              changePage={changePage}
              totalItems={glPosts.count}
              maxItemsPerPage={itemsPerPage}
              currentPageNumber={pageNumber}
              label={`Showing ${glPosts.list.length} of ${glPosts.count} record${
                glPosts.count > 1 ? 's' : ''
              }`}
            />
          </>
        )}
      </Card>
    </>
  )
}
export default GLPostsTable
